import React, { useMemo, useState, useCallback } from 'react';
import {
    Box, Grid, Card, Typography, Button, Link, Chip, Paper,
    AppBar, Toolbar, IconButton, Modal, Backdrop, Fade
} from '@mui/material';
import { makeStyles } from "@material-ui/core";
import {
    Close as CloseIcon, ArrowBackIosNew as ArrowBackIosNewIcon, ArrowForwardIos,
    CheckCircle as CheckCircleIcon, Remove as RemoveIcon, Circle as CircleIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import DownloadPDFButton from "./DownloadPDFButton";
import { useProcessedReportData } from "./useProcessedReportData";
import { formTypes, questionTypes } from '../formBuilder/formBuilderConsts';

const customFormStyles = makeStyles(theme => ({
    reportActions: {
        padding: '1em',
        background: 'white',
        position: 'fixed',
        width: 'fit-content',
        bottom: '2em',
        left: '50%',
        zIndex: 1,
        marginTop: '1rem',
    },
    sectionHeader: {
        fontWeight: 600,
        color: theme.palette.primary.main,
        fontSize: '1.1rem'
    },
    mainBlue: {
        color: theme.palette.primary.main,
    },
    darkBlue: {
        color: theme.palette.primary.dark,
    },
    answer: {
        fontSize: '15px',
    },
}));

const SummaryCard = styled(Link)(({ theme, status }) => ({
    display: 'block',
    textDecoration: 'none',
    padding: theme.spacing(2),
    textAlign: 'center',
    borderRadius: '12px',
    fontWeight: 'bold',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[6],
    },
    ...(status === 'fail' && {
        backgroundColor: '#fdecea',
        border: '1px solid #f5c2c7',
        color: '#7a1e1e',
    }),
    ...(status === 'pass' && {
        backgroundColor: '#e8f5e9',
        border: '1px solid #a5d6a7',
        color: '#1b5e20',
    }),
}));

const KeyValueItem = ({ label, value }) => (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: '10px', background: 'linear-gradient(180deg,#fff,#f7fbff)' }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '.02em' }}>
            {label}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: '600', wordBreak: 'break-word' }}>
            {value || '—'}
        </Typography>
    </Paper>
);

const ImageWithCaption = ({ image, onClick }) => (
    <Grid item xs={6} sm={4} md={3} sx={{ breakInside: 'avoid' }}>
        <Box onClick={() => onClick(image)} sx={{ cursor: 'zoom-in' }}>
            <img
                src={image.url}
                alt={image.type}
                loading="lazy"
                style={{ width: '100%', height: '130px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e7ebf0' }}
            />
            <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                {image.type}
            </Typography>
        </Box>
    </Grid>
);

const QuestionChecklistItem = ({ question }) => {
    const classes = customFormStyles();
    const isFail = question.status === 'fail';

    const renderIcon = () => {
        const iconContainerSx = {
            width: '1.25rem',
            height: '1.25rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mt: '2px',
            mr: 1,
            flexShrink: 0,
            position: 'relative',
        };

        if (isFail) {
            return (
                <Box sx={iconContainerSx}>
                    <CircleIcon sx={{ color: 'error.main', fontSize: '1.25rem' }} />
                    <CloseIcon sx={{
                        color: 'white',
                        fontSize: '1rem',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                    }} />
                </Box>
            );
        }
        if (!!question.answer) {
            return <CheckCircleIcon className={classes.mainBlue} sx={iconContainerSx} />;
        }
        return <RemoveIcon className={classes.mainBlue} sx={iconContainerSx} />;
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', py: 1, borderBottom: '1px dashed #e7ebf0', '&:last-child': { borderBottom: 0 } }}>
            {renderIcon()}
            <Box sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Typography variant="body2" component="strong" sx={{ fontWeight: 500, lineHeight: 1.5 }}>
                        {question.question}
                    </Typography>
                    {isFail && (
                        <Chip label="Fail" color="error" size="small" sx={{ height: 'auto', mt: '2px' }} />
                    )}
                </Box>
                <Typography variant="body2" color={isFail ? "error" : "primary.dark"} className={classes.answer} style={{ fontSize: "15px" }} >
                    {question.displayValue}
                </Typography>
                <Typography variant="body2" color={"primary.dark"} className={classes.answer} style={{ fontSize: "15px" }} >
                    {question?.answer?.note}
                </Typography>
            </Box>
        </Box>
    );
};

const ReportSection = ({ section, onImageClick }) => (
    <Card id={section.name?.toLowerCase().replace(/\s/g, '-')} sx={{ p: 2, mb: 3, boxShadow: 3, borderRadius: '12px' }}>
        <Typography variant="h6" className={customFormStyles().sectionHeader} gutterBottom>{section.name}</Typography>
        <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
                <Typography variant="subtitle2" gutterBottom>Photos</Typography>
                {section.images.length > 0 ? (
                    <Grid container spacing={1.5}>
                        {section.images.map((img, idx) => (
                            <ImageWithCaption key={idx} image={img} onClick={onImageClick} />
                        ))}
                    </Grid>
                ) : (
                    <Typography variant="body2" color="text.secondary">No photos for this section.</Typography>
                )}
            </Grid>
            <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" gutterBottom>Checklist</Typography>
                <Paper variant="outlined" sx={{ p: 1 }}>
                    {section.questions.filter(q => q.questionType !== questionTypes.Image).map(q => <QuestionChecklistItem key={q.id} question={q} />)}
                </Paper>
            </Grid>
        </Grid>
    </Card>
);

const CustomFormOutput = ({
    vehicle,
    answers,
    sections,
    imageTypes,
    generatedDate,
    lot,
    formTypeLabel,
    showFailuresOnly = false,
    formType
}) => {
    const classes = customFormStyles();

    const { metadata, sections: processedSections, heroImageObject, generalVehicleImages } = useProcessedReportData(vehicle, answers, sections, imageTypes, generatedDate, lot, showFailuresOnly);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [currentImage, setCurrentImage] = useState(null);

    const allReportImages = useMemo(() => [
        ...processedSections.flatMap(s => s.images),
        ...generalVehicleImages,
    ], [processedSections, generalVehicleImages]);

    const handleImageClick = useCallback((image) => {
        setCurrentImage(image);
        setLightboxOpen(true);
    }, []);

    const handleCloseLightbox = () => setLightboxOpen(false);

    const navigateLightbox = (direction) => {
        if (!allReportImages.length) return;
        const currentIndex = allReportImages.findIndex(img => img.url === currentImage.url);
        if (currentIndex === -1) return;
        const newIndex = (currentIndex + direction + allReportImages.length) % allReportImages.length;
        setCurrentImage(allReportImages[newIndex]);
    };

    return (
        <Box>
            <Box sx={{ bgcolor: 'background.default', color: 'text.primary' }}>
                <AppBar
                    position="static"
                    sx={{
                        bgcolor: '#ffffff',
                        boxShadow: 3,
                    }}
                >
                    <Toolbar sx={{ p: '0 !important' }}>
                        <Box
                            sx={{
                                flexGrow: 1,
                                display: 'flex',
                                alignItems: 'center',
                                alignSelf: 'stretch',
                                background: 'linear-gradient(to right, #002f5f, #0e67c2 40%, transparent 95%)',
                                pl: { xs: 2, md: 3 },
                            }}
                        >
                            <Typography variant="h6" className={classes.sectionHeader} sx={{ color: 'white', fontWeight: 'bold' }}>
                                Vehicle {formTypeLabel}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', pr: { xs: 2, md: 3 } }}>
                            <Typography
                                variant="body2"
                                className={classes.darkBlue}
                                sx={{
                                    fontWeight: 500,
                                    mr: 1.5,
                                }}
                            >
                                Powered By
                            </Typography>
                            <Box
                                component="img"
                                src="/images/ParqAIlogo_cropped.png"
                                alt="Parq AI Logo"
                                sx={{ height: '32px' }}
                            />
                        </Box>
                    </Toolbar>
                </AppBar>

                <Box sx={{ maxWidth: '1200px', mx: 'auto', p: { xs: 1, sm: 2, md: 3 } }}>
                    {/* --- Hero Section --- */}
                    <Card sx={{ p: 2, mb: 3, boxShadow: 3, borderRadius: '12px' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={7}>
                                <Grid container spacing={1.5}>
                                    <Grid item xs={12} sm={6}><KeyValueItem label="Year, Make, Model" value={`${metadata.year} ${metadata.make} ${metadata.model}`} /></Grid>
                                    <Grid item xs={12} sm={6}><KeyValueItem label="VIN" value={`${metadata.vin}`} /></Grid>
                                    <Grid item xs={12} sm={6}><KeyValueItem label="Asset ID" value={`${metadata.plate}`} /></Grid>
                                    <Grid item xs={12} sm={6}><KeyValueItem label="Odometer" value={`${metadata.mileage}`} /></Grid>
                                    <Grid item xs={12} sm={6}><KeyValueItem label="Inspection Date" value={`${metadata.date}`} /></Grid>
                                    <Grid item xs={12} sm={6}><KeyValueItem label="Location" value={`${metadata.location}`} /></Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <Box
                                    component="img"
                                    src={heroImageObject?.url || ''}
                                    alt={heroImageObject?.type || 'Vehicle'}
                                    onClick={() => heroImageObject && handleImageClick(heroImageObject)}
                                    sx={{ width: '100%', height: { xs: 200, md: '100%' }, objectFit: 'cover', borderRadius: '12px', cursor: 'zoom-in' }}
                                />
                            </Grid>
                        </Grid>
                    </Card>

                    <Grid container spacing={1.5} sx={{ mb: 3 }}>
                        {processedSections.map(section => (
                            <Grid item xs={12} sm={6} md={4} key={section.id}>
                                <SummaryCard href={`#${section.name?.toLowerCase().replace(/\s/g, '-')}`} status={section.status}>
                                    {section.name} — {section.status.charAt(0).toUpperCase() + section.status.slice(1)}
                                </SummaryCard>
                            </Grid>
                        ))}
                    </Grid>

                    {processedSections.map(section => (
                        <ReportSection key={section.id} section={section} onImageClick={handleImageClick} />
                    ))}

                    {generalVehicleImages && formType != formTypes.Activity && generalVehicleImages.length > 0 && (
                        <Card id="vehicle-images" sx={{ p: 2, mb: 3, boxShadow: 3, borderRadius: '12px' }}>
                            <Typography variant="h6" gutterBottom>Vehicle Images</Typography>
                            <Grid container spacing={1.5}>
                                {generalVehicleImages.map((img, idx) => (
                                    <ImageWithCaption key={idx} image={img} onClick={handleImageClick} />
                                ))}
                            </Grid>
                        </Card>
                    )}
                </Box>

                <Modal
                    open={lightboxOpen}
                    onClose={handleCloseLightbox}
                    closeAfterTransition
                    slots={{ backdrop: Backdrop }}
                    componentsProps={{
                        backdrop: {
                            timeout: 500,
                        },
                    }}
                >
                    <Fade in={lightboxOpen} timeout={500}>
                        <Box
                            onClick={(e) => {
                                if (e.target === e.currentTarget) {
                                    handleCloseLightbox();
                                }
                            }}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100vh',
                                width: '100vw',
                                outline: 'none',
                                position: 'relative'
                            }}
                        >
                            <IconButton onClick={() => navigateLightbox(-1)} sx={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: 'white', bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}><ArrowBackIosNewIcon /></IconButton>
                            <IconButton onClick={() => navigateLightbox(1)} sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: 'white', bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}><ArrowForwardIos /></IconButton>
                            <IconButton onClick={handleCloseLightbox} sx={{ position: 'absolute', top: 16, right: 16, color: 'white', bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}><CloseIcon /></IconButton>
                            <Paper sx={{ p: 1, bgcolor: 'transparent', boxShadow: 'none' }}>
                                <img src={currentImage?.url} alt={currentImage?.type} style={{ maxHeight: '90vh', maxWidth: '90vw', borderRadius: '8px' }} />
                                <Typography variant="caption" sx={{ color: 'white', textAlign: 'center', display: 'block', mt: 1 }}>
                                    {currentImage?.type}
                                </Typography>
                            </Paper>
                        </Box>
                    </Fade>
                </Modal>
            </Box>

            <Paper className={classes.reportActions} >
                <DownloadPDFButton
                    vehicle={vehicle}
                    generatedte={generatedDate}
                    sections={sections}
                    answers={answers}
                    imageTypes={imageTypes}
                    lot={lot}
                    formTypeLabel={formTypeLabel}
                    showFailuresOnly={showFailuresOnly}
                    formType={formType}
                />
            </Paper>
        </Box >
    );
};

export default CustomFormOutput;